import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "content"]
  static values = {
    autoOpen: { type: Boolean, default: false },
    scrollOpen: { type: Boolean, default: false },
    scrollThreshold: { type: Number, default: 300 },
    cappingDays: { type: Number, default: 0 },
    cappingPages: { type: Number, default: 0 },
    modalId: String,
    openOnExit: { type: Boolean, default: false },
    openOnBottom: { type: Boolean, default: false },
    priority: { type: Number, default: 0 },
    queueKey: { type: String, default: 'modal_queue' },
    usePortal: { type: Boolean, default: false },
    noIndex: { type: Boolean, default: false }
  }

  initialize() {
    console.log('Modal controller initialized')
    this.boundHandleScroll = this.handleScroll.bind(this)
    this.boundHandleExit = this.handleExitIntent.bind(this)
    this.boundHandleBottom = this.handleBottom.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
  }

  connect() {
    console.log('Modal controller connected', {
      element: this.element,
      modalTarget: this.modalTarget,
      contentTarget: this.contentTarget,
      usePortal: this.usePortalValue,
      modalId: this.modalIdValue
    })

    // Trouver les éléments manuellement
    this.modal = this.element.querySelector('[data-modal-target="modal"]')
    this.content = this.element.querySelector('[data-modal-target="content"]')

    console.log('Elements found:', {
      modal: this.modal,
      content: this.content
    })

    if (!this.modal) {
      console.error('Modal element not found')
      return
    }

    if (!this.hasModalIdValue) {
      console.warn('Modal sans ID détecté. Un ID unique est recommandé pour le capping et la file d\'attente.')
    }

    this.setupEventListeners()

    if (this.shouldShowModal() && this.isAutoModal()) {
      this.registerInQueue()
      this.open()
    }

    // S'abonner aux événements de trigger externes
    document.addEventListener('click', this.handleExternalTrigger.bind(this))
  }

  get modalElement() {
    return this.element.querySelector('[data-modal-target="modal"]')
  }

  get contentElement() {
    return this.element.querySelector('[data-modal-target="content"]')
  }

  open(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.modal) return

    // Vérifier si la modale doit être affichée
    if (!this.shouldShowModal()) return

    // Fermer toutes les autres modales
    document.querySelectorAll('[data-modal-target="modal"]').forEach(modal => {
      if (modal !== this.modal) {
        modal.classList.add('hidden')
        modal.setAttribute('aria-hidden', 'true')
      }
    })

    // Déplacer la modale dans le portail si nécessaire
    if (this.usePortalValue) {
      this.moveToPortal()
    }

    // Afficher la modale
    this.modal.classList.remove('hidden')
    this.modal.classList.add('opacity-100')
    this.modal.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'

    // Animer le contenu
    if (this.content) {
      requestAnimationFrame(() => {
        this.content.classList.remove('scale-95', 'opacity-0')
        this.content.classList.add('scale-100', 'opacity-100')
      })
    }
  }

  showModal() {
    const modal = this.modalElement
    if (!modal) {
      console.error('Cannot show modal: modal element not found')
      return
    }

    modal.classList.remove("hidden")
    modal.classList.add("opacity-100")
    modal.setAttribute("aria-hidden", "false")
    document.body.style.overflow = 'hidden'
    
    const content = this.contentElement
    if (content) {
      requestAnimationFrame(() => {
        content.classList.remove("scale-95", "opacity-0")
        content.classList.add("scale-100", "opacity-100")
      })
    }
  }

  close() {
    if (!this.modal) return

    // Animer la fermeture du contenu
    if (this.content) {
      this.content.classList.remove('scale-100', 'opacity-100')
      this.content.classList.add('scale-95', 'opacity-0')
    }

    // Fermer la modale
    setTimeout(() => {
      this.modal.classList.remove('opacity-100')
      this.modal.classList.add('hidden')
      this.modal.setAttribute('aria-hidden', 'true')
      document.body.style.overflow = 'auto'

      // Remettre la modale à sa place si elle était dans le portail
      if (this.usePortalValue) {
        this.moveFromPortal()
      }
    }, 300)
  }

  // Gestion des événements
  setupEventListeners() {
    // Écouter les clics sur les boutons d'ouverture
    this.element.querySelectorAll('[data-action="click->modal#open"]').forEach(button => {
      button.addEventListener('click', (e) => this.open(e))
    })

    // Écouter les clics sur les boutons de fermeture
    this.element.querySelectorAll('[data-action="modal#close"]').forEach(button => {
      button.addEventListener('click', () => this.close())
    })

    // Écouter les clics sur les boutons de dismiss
    this.element.querySelectorAll('[data-action="modal#dismiss"]').forEach(button => {
      button.addEventListener('click', () => this.dismiss())
    })

    // Écouter les clics en dehors de la modale
    document.addEventListener('click', this.boundHandleClickOutside)

    // Écouter les événements de scroll si nécessaire
    if (this.scrollOpenValue) {
      window.addEventListener('scroll', this.boundHandleScroll)
    }

    // Écouter les événements de sortie si nécessaire
    if (this.openOnExitValue) {
      document.addEventListener('mouseleave', this.boundHandleExit)
    }

    // Écouter les événements de bas de page si nécessaire
    if (this.openOnBottomValue) {
      window.addEventListener('scroll', this.boundHandleBottom)
    }

    // Écouter la touche Escape
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
    document.removeEventListener('click', this.boundHandleClickOutside)
    window.removeEventListener('scroll', this.boundHandleScroll)
    document.removeEventListener('mouseleave', this.boundHandleExit)
    window.removeEventListener('scroll', this.boundHandleBottom)
  }

  // Actions principales
  dismiss() {
    this.setModalDismissed()
    this.close()
  }

  // Animations
  animateClose(modalElement) {
    const contentElement = modalElement.querySelector('[data-modal-target="content"]')
    if (contentElement) {
      contentElement.classList.remove("scale-100", "opacity-100")
      contentElement.classList.add("scale-95", "opacity-0")
    }
    
    setTimeout(() => {
      modalElement.classList.remove("opacity-100")
      modalElement.classList.add("hidden")
      modalElement.setAttribute("aria-hidden", "true")
      document.body.style.overflow = 'auto'
    }, 300)
  }

  // Gestionnaires d'événements
  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  handleClickOutside(event) {
    if (this.modal && !this.modal.contains(event.target) && 
        !event.target.closest('[data-action*="modal#open"]')) {
      this.close()
    }
  }

  handleScroll() {
    if (window.scrollY > this.scrollThresholdValue && !this.isOpen) {
      this.open()
      window.removeEventListener('scroll', this.boundHandleScroll)
    }
  }

  handleExitIntent(event) {
    if (event.clientY <= 0 && !this.isOpen) {
      this.open()
      document.removeEventListener('mouseleave', this.boundHandleExit)
    }
  }

  handleExternalTrigger(event) {
    const trigger = event.target.closest('[data-modal-trigger]')
    if (trigger && trigger.dataset.modalTrigger === this.modalIdValue) {
      event.preventDefault()
      event.stopPropagation()
      this.open()
    }
  }

  handleBottom() {
    if ((window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 100 && !this.isOpen) {
      this.open()
      window.removeEventListener('scroll', this.boundHandleBottom)
    }
  }

  // Gestion de la file d'attente
  registerInQueue() {
    console.log('📝 Tentative d\'enregistrement dans la file d\'attente', {
      modalId: this.modalIdValue,
      priority: this.priorityValue,
      queueKey: this.queueKeyValue
    })

    const queue = this.getQueue()
    console.log('📋 File d\'attente actuelle:', queue)

    // Si la modal est déjà dans la file d'attente, on l'affiche directement
    if (queue.some(modal => modal.id === this.modalIdValue)) {
      console.log('⚠️ La modale est déjà dans la file d\'attente')
      return true
    }

    queue.push({
      id: this.modalIdValue,
      priority: this.priorityValue
    })

    queue.sort((a, b) => b.priority - a.priority)
    
    this.saveQueue(queue)
    console.log('✅ Modale enregistrée dans la file d\'attente')
    console.log('📋 Nouvelle file d\'attente:', this.getQueue())
    
    return true
  }

  removeFromQueue() {
    const queue = this.getQueue()
    localStorage.setItem(
      this.queueKeyValue, 
      JSON.stringify(queue.filter(item => item.id !== this.modalIdValue))
    )
  }

  showNextInQueue() {
    const queue = this.getQueue()
    const nextModal = queue[0] && document.querySelector(
      `[data-modal-modal-id-value="${queue[0].id}"]`
    )
    
    if (nextModal) {
      const controller = this.application.getControllerForElementAndIdentifier(nextModal, 'modal')
      controller?.showModal()
    }
  }

  // Gestion du capping
  shouldShowModal() {
    if (!this.hasModalIdValue) return true
    
    const dismissedData = this.getDismissedData()
    if (!dismissedData) return true

    const { dismissedUntil, pagesVisited, maxPages } = dismissedData
    const isTimeExpired = Date.now() > parseInt(dismissedUntil)
    
    return this.cappingPagesValue > 0 
      ? isTimeExpired || pagesVisited >= maxPages 
      : isTimeExpired
  }

  setModalDismissed() {
    if (!this.hasModalIdValue) return

    const dismissUntil = new Date()
    dismissUntil.setDate(dismissUntil.getDate() + this.cappingDaysValue)

    const dismissedData = {
      dismissedUntil: dismissUntil.getTime().toString(),
      pagesVisited: 0,
      maxPages: this.cappingPagesValue
    }

    localStorage.setItem(
      `modal_${this.modalIdValue}_dismissed_data`, 
      JSON.stringify(dismissedData)
    )
  }

  // Utilitaires
  getQueue() {
    return JSON.parse(localStorage.getItem(this.queueKeyValue) || '[]')
  }

  get isOpen() {
    return !this.modalElement.classList.contains("hidden")
  }

  isAnyModalOpen() {
    return document.querySelector('.modal[aria-hidden="false"]') !== null
  }

  isAutoModal() {
    return this.autoOpenValue || 
           this.scrollOpenValue || 
           this.openOnExitValue || 
           this.openOnBottomValue
  }

  closeAllModals() {
    document.querySelectorAll('.modal[aria-hidden="false"]').forEach(modal => {
      const controller = this.application.getControllerForElementAndIdentifier(modal, 'modal')
      if (controller && controller !== this) {
        controller.close()
      }
    })
  }

  incrementPageVisit() {
    if (!this.hasModalIdValue) return

    const dismissedData = this.getDismissedData()
    if (dismissedData?.pagesVisited < dismissedData?.maxPages) {
      dismissedData.pagesVisited += 1
      localStorage.setItem(
        `modal_${this.modalIdValue}_dismissed_data`, 
        JSON.stringify(dismissedData)
      )
    }
  }

  getDismissedData() {
    const data = localStorage.getItem(`modal_${this.modalIdValue}_dismissed_data`)
    return data ? JSON.parse(data) : null
  }

  cleanQueue() {
    const queue = this.getQueue()
    const cleanedQueue = queue.filter(modal => {
      // Supprimer les modales plus anciennes que 24h
      if (modal.timestamp) {
        const isExpired = Date.now() - modal.timestamp > 24 * 60 * 60 * 1000
        if (isExpired) return false
      }
      
      // Garder uniquement les modales avec le préfixe roadbook- ou sans préfixe
      return modal.id.startsWith('roadbook-') || !modal.id.includes('-')
    })
    
    this.saveQueue(cleanedQueue)
    console.log('🧹 File d\'attente nettoyée:', cleanedQueue)
  }

  saveQueue(queue) {
    localStorage.setItem(this.queueKeyValue, JSON.stringify(queue))
  }

  moveToPortal() {
    // Créer le conteneur de portail s'il n'existe pas
    if (!document.getElementById('modal-portal')) {
      const portal = document.createElement('div')
      portal.id = 'modal-portal'
      portal.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; z-index: 9999;'
      document.body.appendChild(portal)
    }
    
    // Déplacer la modal dans le portail
    const portal = document.getElementById('modal-portal')
    if (this.modal && portal) {
      // Sauvegarder la position d'origine
      this.originalParent = this.modal.parentNode
      this.originalNextSibling = this.modal.nextSibling
      
      // Déplacer dans le portail
      portal.appendChild(this.modal)
    }
  }

  moveFromPortal() {
    if (this.modal && this.originalParent) {
      // Remettre à la position d'origine
      if (this.originalNextSibling) {
        this.originalParent.insertBefore(this.modal, this.originalNextSibling)
      } else {
        this.originalParent.appendChild(this.modal)
      }
    }
  }

  disconnect() {
    this.removeEventListeners()
    this.incrementPageVisit()
    document.removeEventListener('click', this.handleExternalTrigger.bind(this))

    // Si la modal est dans le portail, la remettre à sa position d'origine
    if (this.usePortalValue) {
      this.moveFromPortal()
    }
  }
}
